import type { SajuAnalysis } from '../types/input';
import type { SangsaengjiCross } from '../types/compatibility';
import type { TenGod } from '../types/ten-gods';
import { getHiddenStems } from '../core/hidden-stems';
import { getTenGod } from '../analysis/ten-gods';

const FOOD_STARS: TenGod[] = ['식신', '상관'];
const WEALTH_STARS: TenGod[] = ['편재', '정재'];

/**
 * 상생지(相生支) 교차 분석.
 *
 * 자평 전통: 상대의 일지 지장간이 내 식신/상관(자기 표현·자식)이거나
 * 재성(물질·배우자)에 해당하면 "상대가 나의 토양이 되는" 상생 관계로 본다.
 *
 * 양방향으로 카운트하여 조화도를 산출.
 */
export function analyzeSangsaengji(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): SangsaengjiCross {
  const p1 = countFromPartnerDayBranch(analysis1.dayMaster, analysis2.fourPillars.day.branch);
  const p2 = countFromPartnerDayBranch(analysis2.dayMaster, analysis1.fourPillars.day.branch);

  const p1Sum = p1.foodStars + p1.wealthStars;
  const p2Sum = p2.foodStars + p2.wealthStars;
  const total = p1Sum + p2Sum;
  // '매우좋음'은 일방적이지 않아야 — 양쪽 모두 최소 1개 이상 + 합 3+
  let harmony: SangsaengjiCross['harmony'];
  if (p1Sum >= 1 && p2Sum >= 1 && total >= 3) harmony = '매우좋음';
  else if (total >= 1) harmony = '좋음';
  else harmony = '보통';

  const description =
    `상생지: P1←P2일지(식상 ${p1.foodStars}/재성 ${p1.wealthStars}), ` +
    `P2←P1일지(식상 ${p2.foodStars}/재성 ${p2.wealthStars}) — ${harmony}`;

  return { person1FromPartner: p1, person2FromPartner: p2, harmony, description };
}

function countFromPartnerDayBranch(
  myDayMaster: SajuAnalysis['dayMaster'],
  partnerDayBranch: SajuAnalysis['fourPillars']['day']['branch'],
): { foodStars: number; wealthStars: number } {
  const hidden = getHiddenStems(partnerDayBranch);
  let foodStars = 0;
  let wealthStars = 0;
  for (const stem of hidden) {
    const tg = getTenGod(myDayMaster, stem);
    if (FOOD_STARS.includes(tg)) foodStars++;
    else if (WEALTH_STARS.includes(tg)) wealthStars++;
  }
  return { foodStars, wealthStars };
}
